import BlogArticle from '@/components/feature/BlogArticle';

const props = {
  "slug": "double-vitrage-embue",
  "category": "Vitrage",
  "seoTitle": "Double vitrage embué : pourquoi et comment y remédier",
  "metaDescription": "Double vitrage embué à Lyon ? Pourquoi la buée apparaît entre les vitres, ce que ça change pour l'isolation et comment remplacer le vitrage.",
  "h1": "Double vitrage embué : pourquoi ça arrive et comment y remédier",
  "datePublished": "2026-07-21",
  "dateLabel": "21 juillet 2026",
  "readingTime": "6 min",
  "heroImage": "/images/vitrage-embue-hero-2026.jpg",
  "heroAlt": "Double vitrage embué à Lyon, buée entre les deux verres",
  "ctaTitle": "Vitrage embué qui perd son isolation ?",
  "ctaText": "DFPRO Lyon remplace le vitrage, pas toute la fenêtre. Devis gratuit.",
  "intro": [
    "De la buée qui apparaît <strong>entre les deux vitres</strong> d'un double vitrage, impossible à essuyer d'un côté ou de l'autre : c'est un signe qui ne trompe pas. Le vitrage isolant a perdu son étanchéité. Ce phénomène, très fréquent, n'est pas qu'un désagrément visuel — il a des conséquences directes sur le confort et sur votre facture de chauffage.",
    "Beaucoup de propriétaires craignent de devoir changer toute la fenêtre. Dans la grande majorité des cas, ce n'est pas nécessaire. Voici pourquoi la buée se forme, ce qu'elle révèle sur l'état de votre vitrage, et la solution réelle."
  ],
  "sections": [
    {
      "h2": "Comment fonctionne un double vitrage",
      "body": [
        "Un double vitrage est constitué de deux verres séparés par une lame d'air ou de gaz (souvent de l'argon), maintenue par un intercalaire et scellée par un joint périphérique étanche. C'est cette lame isolante qui donne au double vitrage sa performance thermique.",
        "À l'intérieur de l'intercalaire se trouve un déshydratant qui absorbe l'humidité résiduelle. Tant que le joint est intact, aucune humidité n'entre et l'espace reste parfaitement sec et transparent."
      ]
    },
    {
      "h2": "Pourquoi de la buée apparaît entre les verres",
      "body": [
        "Avec le temps, le joint périphérique se dégrade : micro-fissures, décollement, vieillissement du mastic. L'étanchéité est rompue, l'air humide extérieur pénètre, et le déshydratant finit par saturer. La vapeur d'eau se condense alors <strong>entre les deux verres</strong>, exactement là où l'on ne peut pas l'essuyer.",
        "Ce n'est pas un défaut d'entretien de votre part : c'est l'usure normale du scellement, accélérée par les écarts de température, les UV et l'humidité. À Lyon, l'alternance d'étés chauds et d'hivers humides sollicite particulièrement les joints des vitrages."
      ]
    },
    {
      "h2": "Les conséquences sur l'isolation",
      "body": [
        "Une fois le joint rompu, le gaz isolant s'échappe progressivement et la lame se charge d'humidité. Le vitrage perd alors une part importante de son pouvoir isolant : sensation de paroi froide, déperditions de chaleur accrues, et parfois condensation persistante qui peut, à terme, marquer les verres.",
        "Autrement dit, la buée est le symptôme visible d'un vitrage qui ne joue plus son rôle thermique. Plus on tarde, plus l'inconfort et les pertes de chaleur s'installent."
      ]
    },
    {
      "h2": "La solution : remplacer le vitrage, pas la fenêtre",
      "body": [
        "Bonne nouvelle : dans l'immense majorité des cas, on remplace uniquement le <strong>vitrage</strong>, en conservant le châssis et l'ouvrant. Le <a href=\"/double-vitrage-embue-lyon/\" class=\"text-primary-600 font-medium underline\">traitement d'un double vitrage embué</a> consiste à déposer le vitrage défaillant et à poser un vitrage isolant neuf, sur mesure.",
        "Le <a href=\"/remplacement-double-vitrage-lyon/\" class=\"text-primary-600 font-medium underline\">remplacement du double vitrage</a> restaure l'isolation d'origine à un coût bien inférieur à celui d'une fenêtre complète. Le remplacement total du châssis ne s'impose que si le cadre lui-même est dégradé."
      ]
    },
    {
      "h2": "Comment se passe l'intervention",
      "body": [
        "Le technicien prend d'abord les mesures précises et relève le type de vitrage (épaisseur, gaz, éventuel traitement isolation renforcée ou phonique). Le vitrage est ensuite fabriqué sur mesure, puis posé lors d'un second passage.",
        "DFPRO Lyon réalise ce remplacement sur les fenêtres PVC, aluminium et bois, à Lyon et dans la métropole — devis gratuit, garantie 10 ans."
      ]
    }
  ],
  "faq": [
    {
      "q": "Peut-on désembuer un double vitrage soi-même ?",
      "a": "Non. La buée est enfermée entre deux verres scellés : aucun produit, aucune astuce ne la fait disparaître durablement, et les kits de « perçage-séchage » ne restaurent pas l'isolation. La seule solution fiable est le remplacement du vitrage isolant."
    },
    {
      "q": "Faut-il changer toute la fenêtre ?",
      "a": "Le plus souvent, non. On remplace uniquement le vitrage embué en conservant le cadre et l'ouvrant, ce qui est bien plus économique. Le remplacement complet ne se justifie que si le châssis est lui-même abîmé ou trop ancien."
    },
    {
      "q": "Combien de temps pour l'intervention à Lyon ?",
      "a": "Après prise de mesures, le vitrage est fabriqué sur mesure puis posé. DFPRO Lyon vous communique un délai précis dès le devis, qui est gratuit. Appelez le 09 80 80 36 84."
    },
    {
      "q": "Un vitrage embué est-il dangereux ?",
      "a": "Il n'est pas dangereux en soi, mais il signale une perte d'isolation qui augmente vos déperditions de chaleur et peut, à la longue, marquer les verres. Le traiter rapidement évite l'inconfort et les surcoûts de chauffage."
    }
  ],
  "related": [
    {
      "title": "Fenêtre qui ferme mal : crémone, réglage et solutions",
      "href": "/blog/fenetre-qui-ferme-mal/"
    },
    {
      "title": "Baie vitrée qui coince : roulettes, rail et réglage",
      "href": "/blog/baie-vitree-qui-coince/"
    }
  ]
};

export default function DoubleVitrageEmbue() {
  return <BlogArticle {...props} />;
}
